<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { state, selected, getObsSpecs, actions, params } from '@/components/Ajuda/Montador/composables/useBuilderState'
import { parseNumberList, normalizeListInputLive, finalizeListInput,
         parseDecimalBR, formatDecimalBR, formatListSemicolon } from '@/components/Ajuda/Montador/lib/numbers'
import { getComboFaces, setComboFaces } from '@/components/Ajuda/Montador/composables/useBuilderState'


const groupId = computed(() => selected.value?.comboGroup ?? null)
const gapsCount = computed(() => {
  if (!groupId.value) return 0
  return state.obstacles.filter(o => o.comboGroup === groupId.value).length - 1
})

/* ------- estado local de edição ------- */
const form = reactive({
  wm: '', hm: '', angle: '', A: '', D: '',
  thicks: '', gaps: '', heights: ''
})

const facesForm = reactive({ arr: [] })

// <<< NOVO: mantém o grupo da edição atual
const editingFaces = reactive({ groupId: null })

watch([groupId, gapsCount], () => {
  if (!groupId.value || gapsCount.value <= 0) { facesForm.arr = []; editingFaces.groupId = null; return }
  facesForm.arr = getComboFaces(groupId.value, gapsCount.value).map(v => formatDecimalBR(v, 2))
  // ao trocar de combo, zera “grupo em edição”
  editingFaces.groupId = null
}, { immediate: true })

function aplicarFaces(targetGroupId = null) {
  const gid = targetGroupId ?? editingFaces.groupId ?? groupId.value
  if (!gid) return
  const parsed = facesForm.arr.map(parseDecimalBR).filter(v => Number.isFinite(v))
  setComboFaces(gid, parsed)

  // Reflow do combo ancorado em um item do próprio grupo
  const anchor = (selected.value?.comboGroup === gid
    ? selected.value
    : state.obstacles.find(o => o.comboGroup === gid))
  if (anchor) actions.reflowComboFromChanged(anchor)
}

// Handlers de edição — garantem que salvamos no combo correto
function onFaceFocus() {
  editingFaces.groupId = groupId.value  // trava o grupo no início da edição
}
function onFaceBlur() {
  aplicarFaces(editingFaces.groupId)    // salva no grupo “travado”
  editingFaces.groupId = null
}
function onFaceEnter() {
  aplicarFaces(editingFaces.groupId)
  editingFaces.groupId = null
}

const selType = computed(() => (selected.value?.type || '').toString().toLowerCase())

// Tipos que possuem "vãos"
const showGapsField = computed(() => ['oxer','triplebar','triple'].includes(selType.value))

// (se usa v-if no template)

const normType = (t) => t === 'wall' ? 'muro' : (t === 'triple' ? 'triplebar' : t)

const showHeightsField = computed(() =>
  ['vertical','oxer','triplebar','plank','gate','muro'].includes(normType(selType.value))
)

// Extrai alturas de barra do objeto de Parâmetros (aceita vários nomes/formatos)
// function extractBarHeightsFromBase(base = {}) {
//   const out = []

//   // arrays diretos
//   if (Array.isArray(base.heights))        out.push(...base.heights)
//   if (Array.isArray(base.barHeights))     out.push(...base.barHeights)
//   if (Array.isArray(base.alturas))        out.push(...base.alturas)
//   if (Array.isArray(base?.bars?.heights)) out.push(...base.bars.heights)

//   // strings tipo "0,60;1,00"
//   const tryParseStr = (v) => {
//     if (typeof v === 'string' && v.trim()) {
//       const arr = parseNumberList(v)   // já lida com vírgula decimal e ';'
//       if (Array.isArray(arr)) out.push(...arr)
//     }
//   }
//   tryParseStr(base.heights)
//   tryParseStr(base.barHeights)
//   tryParseStr(base.alturas)
//   tryParseStr(base.heightsStr)
//   tryParseStr(base.alturasStr)

//   return out.map(Number).filter(Number.isFinite)
// }

// Alturas base vindas **só** dos Parâmetros (sem cair para hm), evitando duplicidades
const baseBarHeights = computed(() => {
  const base = params?.obstacles?.[normType(selType.value)] || {}

  // pega a primeira fonte não vazia (prioridade: heights > barHeights > ...)
  const pick = (...cands) => {
    for (const v of cands) {
      if (Array.isArray(v) && v.length) return v
      if (typeof v === 'string' && v.trim()) {
        const arr = parseNumberList(v) || []
        if (arr.length) return arr
      }
    }
    return []
  }

  const chosen = pick(
    base.heights, base.barHeights, base.alturas, base?.bars?.heights,
    base.heightsStr, base.barHeightsStr, base.alturasStr
  )

  // colapsa padrões repetidos: [a,b,a,b] (oxer) ou [a,b,a,b,a,b] (triplo)
  const collapseRepeated = (arr) => {
    const n = arr.length
    for (const k of [3, 2]) { // tenta triplo, depois duplo
      if (n % k === 0) {
        const block = arr.slice(0, n / k)
        let ok = true
        for (let i = 1; i < k; i++) {
          const from = i * (n / k)
          for (let j = 0; j < block.length; j++) {
            if (+block[j] !== +arr[from + j]) { ok = false; break }
          }
          if (!ok) break
        }
        if (ok) return block
      }
    }
    return arr
  }

  return collapseRepeated(chosen).map(Number).filter(Number.isFinite)
})

// Se o tipo NÃO tiver gaps, apague eventual override e limpe o input
watch(showGapsField, (canHave) => {
  if (!canHave && selected.value) {
    const loc = ensureLocal(selected.value)
    delete loc.gaps
    form.gaps = ''
  }
}, { immediate: true })


// mostrar hm só onde faz sentido (oculta em oxer/triple/start/finish)
// const showHm = computed(() => !['oxer','triplebar','triple','start','finish'].includes(selType.value))

// evitar sobrescrever enquanto o usuário edita hm
// const editingDims = reactive({ hm:false })

// auto-sincronizar hm com alturas informadas (se houver)
// só roda quando não estiver editando hm
// watch(() => state.editLists.heights, (s) => {
//   if (!selected.value || !showHm.value || editingDims.hm) return
//   const H = parseNumberList(s)
//   if (H.length) {
//     const maxH = Math.max(...H)
//     selected.value.local = selected.value.local || {}
//     selected.value.local.hm = maxH
//     form.hm = formatDecimalBR(maxH)
//   }
// })


/* ------- helpers de display ------- */
function angleVsBaseC(rotDeg){ let a=((rotDeg%180)+180)%180; return a>90?180-a:a }
function distABCD(x,y,W,H){ return { A:y, B:Math.max(0,W-x), C:Math.max(0,H-y), D:x } }
function clamp(v, a, b){ return Math.max(a, Math.min(b, v)) }


/* ------- sync do selecionado para o form ------- */
const spec = computed(() => selected.value ? getObsSpecs(selected.value) : null)
const abcd = computed(() => selected.value ? distABCD(selected.value.xm, selected.value.ym, Number(state.arenaWm||0), Number(state.arenaHm||0)) : null)

watch(selected, () => {
  const o = selected.value; if (!o) { Object.assign(form,{wm:'',hm:'',angle:'',A:'',D:'',thicks:'',gaps:'',heights:''}); return }
  const s = getObsSpecs(o)

  form.wm    = formatDecimalBR(s.wm)
  form.hm    = formatDecimalBR(s.hm)
  form.angle = formatDecimalBR(angleVsBaseC(o.rot), 1)
  form.A     = formatDecimalBR(o.ym)
  form.D     = formatDecimalBR(o.xm)

  // sincroniza listas com o composable
  actions.setEditsFromSelected()
  const sTh  = (state.editLists.thicks  || '').trim()
  const sGap = (state.editLists.gaps    || '').trim()
  const sH   = (state.editLists.heights || '').trim()

  // 1) Espessuras
  if (sTh) {
    form.thicks = sTh
  } else {
    const arr = (s.thicknesses || []).map(Number).filter(Number.isFinite)
    form.thicks = arr.length ? formatListSemicolon(arr, 2) : ''
  }

  // 2) Vãos — somente para Oxer/Triple
  if (showGapsField.value) {
    if (sGap) form.gaps = sGap
    else {
      const arr = (s.gaps || []).map(Number).filter(Number.isFinite)
      form.gaps = arr.length ? formatListSemicolon(arr, 2) : ''
    }
  } else {
    form.gaps = ''
  }

  // 3) Alturas — prioridade: override (sH) > s.heights > baseBarHeights (Parâmetros).
  //    Nunca cai para s.hm aqui.
  if (sH) {
    form.heights = sH
  } else {
    let heightsArr = []
    if (Array.isArray(s.heights) && s.heights.length) {
      heightsArr = s.heights
    } else {
      heightsArr = baseBarHeights.value
    }
    heightsArr = heightsArr.map(Number).filter(Number.isFinite)
    form.heights = heightsArr.length ? formatListSemicolon(heightsArr, 2) : ''
  } // DEBUG: valor final que foi para o input
 console.log('[HeightsDebug] final form.heights =', form.heights)

}, { immediate: true })

/* ------- aplicar alterações ------- */
function ensureLocal(o){
  o.local = o.local || {}
  return o.local
}

// flags de edição dos campos A/D (para não “pisar” enquanto digita)
const editingPos = reactive({ A: false, D: false })

// quando o obstáculo se move (drag), atualiza os inputs A/D se não estiver editando
watch([() => selected.value?.ym, () => selected.value?.xm], ([ym, xm]) => {
  if (!selected.value) return
  if (!editingPos.A) form.A = formatDecimalBR(ym, 2)
  if (!editingPos.D) form.D = formatDecimalBR(xm, 2)
})

function onAFocus(ev){ editingPos.A = true; ev.target?.select?.() }
function onDFocus(ev){ editingPos.D = true; ev.target?.select?.() }

function onABlur(){
  applyAD()                      // grava o que o usuário editou
  editingPos.A = false
  if (selected.value) form.A = formatDecimalBR(selected.value.ym, 2)
}
function onDBlur(){
  applyAD()
  editingPos.D = false
  if (selected.value) form.D = formatDecimalBR(selected.value.xm, 2)
}


/* ===== Percurso (ajustes locais) ===== */
const courseArr = computed(() => state.tools?.course || [])

const segIndex = computed(() => {
  const id = selected.value?.id
  if (id == null) return -1
  const arr = courseArr.value
  return arr.findIndex(s => (typeof s === 'number' ? s : s.id) === id)
})

const segOut = computed({
  get() {
    const idx = segIndex.value
    if (idx < 0) return null
    const arr = courseArr.value
    const cur = arr[idx]
    if (typeof cur === 'number') { arr[idx] = { id: cur }; return arr[idx] }
    return cur
  }
})

const segSelf = computed({
  get() {
    const idx = segIndex.value
    if (idx < 0) return null
    const arr = courseArr.value
    const cur = arr[idx]
    if (typeof cur === 'number') { arr[idx] = { id: cur }; return arr[idx] }
    return cur
  }
})

const inCourse = computed(() => segIndex.value >= 0)

/* sliders (0..1) — Curvatura/Suavidade */
const uiTension = computed({
  get: () => (segOut.value && typeof segOut.value.pathTension === 'number')
              ? segOut.value.pathTension : state.tools.pathTension,
  set: v => { if (segOut.value && Number.isFinite(+v)) segOut.value.pathTension = clamp(+v, 0, 1) }
})
const uiSmooth = computed({
  get: () => (segOut.value && typeof segOut.value.curveSmoothness === 'number')
              ? segOut.value.curveSmoothness : state.tools.curveSmoothness,
  set: v => { if (segOut.value && Number.isFinite(+v)) segOut.value.curveSmoothness = clamp(+v, 0, 1) }
})


/* campos com vírgula — Saída/Entrada (passadas) e Raio mínimo */

// ⬇ strings locais (não formatar enquanto digita)
const formCourse = reactive({ exit: '', entry: '', rmin: '' })
const editing    = reactive({ exit: false, entry: false, rmin: false })

watch([segOut, segSelf], () => {
  if (!editing.exit)
    formCourse.exit  = formatDecimalBR(segOut.value?.exitStrides ?? state.tools.exitStrides, 1)
  if (!editing.entry)
    formCourse.entry = formatDecimalBR(segSelf.value?.entryStrides ?? state.tools.entryStrides, 1)
  if (!editing.rmin)
    formCourse.rmin  = formatDecimalBR(segOut.value?.rMin ?? state.tools.rMin, 1)
}, { immediate: true })

function onExitInput(e) {
  formCourse.exit = e.target.value
  const v = parseDecimalBR(formCourse.exit)
  if (segOut.value && Number.isFinite(v)) segOut.value.exitStrides = v   // ✅ nome correto
}
function onEntryInput(e) {
  formCourse.entry = e.target.value
  const v = parseDecimalBR(formCourse.entry)
  if (segSelf.value && Number.isFinite(v)) segSelf.value.entryStrides = v
}
function onRMinInput(e) {
  formCourse.rmin = e.target.value
  const v = parseDecimalBR(formCourse.rmin)
  if (segOut.value && Number.isFinite(v)) segOut.value.rMin = Math.max(0, v)
}

// foco/blur (seleciona tudo e normaliza só ao sair)
function onExitFocus(ev){ editing.exit = true;  ev.target.select() }
function onEntryFocus(ev){ editing.entry = true; ev.target.select() }
function onRMinFocus(ev){  editing.rmin = true;  ev.target.select() }

function onExitBlur(){
  editing.exit = false
  if (segOut.value) formCourse.exit = formatDecimalBR(segOut.value.exitStrides, 1)
}
function onEntryBlur(){
  editing.entry = false
  if (segSelf.value) formCourse.entry = formatDecimalBR(segSelf.value.entryStrides, 1)
}
function onRMinBlur(){
  editing.rmin = false
  if (segOut.value) formCourse.rmin = formatDecimalBR(segOut.value.rMin, 1)
}

// --- edição do campo Ângulo vs C (evita sobrescrever enquanto digita)
const editingAngle = ref(false)

watch(() => selected.value?.rot, (rot) => {
  if (!selected.value) return
  if (editingAngle.value) return              // não pisar enquanto o usuário digita
  form.angle = formatDecimalBR(angleVsBaseC(rot ?? 0), 1)
})

// Sempre que o tipo não tiver "vãos", limpa o override e esvazia o input
watch(showGapsField, (canHave) => {
  if (!canHave && selected.value) {
    const loc = ensureLocal(selected.value)
    delete loc.gaps
    form.gaps = ''
  }
}, { immediate: true })


function onAngleFocus(ev){
  editingAngle.value = true
  ev.target?.select?.()
}
function onAngleBlur(){
  editingAngle.value = false
  applyAngle()  // grava o que foi digitado em o.rot
  if (selected.value) {
    // re-normaliza a exibição com base no rot final
    form.angle = formatDecimalBR(angleVsBaseC(selected.value.rot), 1)
  }
}

/** Dimensões (wm, hm) */
function applyDims(){
  const o = selected.value; if (!o) return
  const loc = ensureLocal(o)
  const wm = parseDecimalBR(form.wm)
  const hm = parseDecimalBR(form.hm)
  if (Number.isFinite(wm)) loc.wm = clamp(wm, 0.2, 20)
  if (Number.isFinite(hm)) loc.hm = clamp(hm, 0.2, 20)
}

/** Posição via A/D (A=ym, D=xm) */
function applyAD(){
  const o = selected.value; if (!o) return
  const A = parseDecimalBR(form.A)
  const D = parseDecimalBR(form.D)
  if (Number.isFinite(A)) o.ym = clamp(A, 0, Number(state.arenaHm||0))
  if (Number.isFinite(D)) o.xm = clamp(D, 0, Number(state.arenaWm||0))
}

/** Ângulo vs C → rot (mantém quadrante) */
function applyAngle(){
  const o = selected.value; if (!o) return
  const a = clamp(parseDecimalBR(form.angle) || 0, 0, 90)
  const r = ((o.rot % 360) + 360) % 360        // 0..359
  const r180 = r % 180                         // 0..179
  const cand = [a, 180 - a]                    // dois ângulos com mesma “distância” a C
  let c = cand[0]
  if (Math.abs(cand[1] - r180) < Math.abs(c - r180)) c = cand[1]
  const base = Math.floor(r / 180) * 180
  o.rot = base + c
  form.angle = formatDecimalBR(a, 1)
}

/** Frente (→/←) */
function toggleFront(){
  const o = selected.value; if (!o) return
  const loc = ensureLocal(o)
  const cur = (getObsSpecs(o).dir === -1 ? -1 : 1)
  loc.dir = (cur === -1 ? 1 : -1)
}

/** Espessuras/Vãos/Alturas (listas) */
function onListInput(which, ev){
  const v = normalizeListInputLive(ev?.target?.value ?? '')
  form[which] = v
}

function onListBlur(which){
  form[which] = finalizeListInput(form[which])
  applyLists()                // ✅ grava em selected.local.{thicknesses,gaps,heights}
}

function applyLists(){
  const o = selected.value; if (!o) return
  const loc = ensureLocal(o)
  const th = parseNumberList(form.thicks)
  const gp = parseNumberList(form.gaps)
  const hh = parseNumberList(form.heights)
  if (th.length) loc.thicknesses = th; else delete loc.thicknesses
  if (gp.length) loc.gaps        = gp; else delete loc.gaps
  if (hh.length) loc.heights     = hh; else delete loc.heights
  // mantém os inputs sincronizados com o composable
  actions.setEditsFromSelected()
}

/* ------- derivados ------- */
const thicks = computed(() => (spec.value?.thicknesses || []).map(Number).filter(Number.isFinite))
const gaps   = computed(() => (spec.value?.gaps || []).map(Number).filter(Number.isFinite))
const heights = computed(() => (spec.value?.heights || []).map(Number).filter(Number.isFinite))
const c2c = computed(() => {
  const t = thicks.value, g = gaps.value
  return g.map((gi, i) => gi + (t[i] + (t[i+1] ?? t[i]))/2)
})
const dirArrow = computed(() => (spec.value?.dir === -1 ? '←' : '→'))
</script>

<template>
  <div class="section compact">
    <label class="label">Selecionado</label>

    <div v-if="!selected">
      <div class="hint">Nenhum obstáculo selecionado.</div>
    </div>

    <template v-else>
      <div class="hint"><strong>Tipo:</strong> {{ (selected.type || '').toString() }}</div>
      <div class="hint"><strong>ID:</strong> {{ selected.id }}</div>
      <div class="hint"><strong>Rótulo:</strong> {{ selected.label || '—' }}</div>

      <!-- Dimensões e orientação -->
      <div class="row two mt6">
        <div class="flex-fill">
          <label class="mini-label">Largura (wm)</label>
          <input class="ctrl" v-model="form.wm" @blur="applyDims" inputmode="decimal" pattern="[0-9]+([,][0-9]+)?" />
        </div>
      </div>

      <div class="row two">
        <div class="flex-fill">
          <label class="mini-label">Ângulo vs C (°)</label>
          <input class="ctrl" v-model="form.angle" @focus="onAngleFocus" @blur="onAngleBlur" />
        </div>
        <div class="flex-fill">
          <label class="mini-label">Frente</label>
          <div class="row one">
            <button class="btn btn-slim" @click="toggleFront">Inverter ({{ dirArrow }})</button>
          </div>
        </div>
      </div>

      <!-- Centro A/D -->
      <div class="row two mt6">
        <div class="flex-fill">
          <label class="mini-label">A (m)</label>
          <input
            class="ctrl"
            inputmode="decimal"
            pattern="[0-9]+([,][0-9]+)?"
            v-model="form.A"
            @focus="onAFocus"
            @blur="onABlur"
          />
        </div>
        <div class="flex-fill">
          <label class="mini-label">D (m)</label>
          <input
            class="ctrl"
            inputmode="decimal"
            pattern="[0-9]+([,][0-9]+)?"
            v-model="form.D"
            @focus="onDFocus"
            @blur="onDBlur"
          />
        </div>
      </div>
      <div class="hint kv">
        <span class="k">Centro A/B/C/D:</span>
        <span class="v">
          A {{ formatDecimalBR(abcd.A) }} | B {{ formatDecimalBR(abcd.B) }} | C {{ formatDecimalBR(abcd.C) }} | D {{ formatDecimalBR(abcd.D) }}
        </span>
      </div>

      <!-- Listas -->
      <div class="section-individual mt8">
        <div class="mini-label">Listas estruturais (overrides locais)</div>

        <!-- Espessuras -->
        <div class="row one">
          <div class="flex-fill">
            <label class="mini-label">Espessuras (m)</label>
            <input
              class="ctrl mono"
              :value="form.thicks"
              @input="onListInput('thicks', $event)"
              @blur="onListBlur('thicks')"
              @keyup.enter="applyLists"
              placeholder="0,10;0,10;0,10"
            />
          </div>
        </div>

        <!-- Vãos (apenas Oxer/Triple) -->
        <div class="row one" v-if="showGapsField">
          <div class="flex-fill">
            <label class="mini-label">Vãos (m)</label>
            <input
              class="ctrl mono"
              :value="form.gaps"
              @input="onListInput('gaps', $event)"
              @blur="onListBlur('gaps')"
              @keyup.enter="applyLists"
              placeholder="0,50;0,60"
            />
          </div>
        </div>

        <div class="row one" v-if="showHeightsField">
          <div class="flex-fill">
            <label class="mini-label">Alturas (m)</label>
            <input 
              class="ctrl mono" 
              :value="form.heights"
              @input="onListInput('heights', $event)" 
              @blur="onListBlur('heights')"
              @keyup.enter="applyLists"
              placeholder="0,60;0,80;1,00" />
          </div>
        </div>

        <!-- Distâncias do Combo -->
        <div v-if="groupId" class="section combo-faces">
          <label class="label">Distâncias do Combo (m)</label>

          <div class="row two" style="margin-top:6px">
            <div class="flex-fill">
              <label class="mini-label">Entre 1 e 2 (m)</label>
              <input
                class="ctrl"
                v-model="facesForm.arr[0]"
                @focus="onFaceFocus"
                @blur="onFaceBlur"
                @keydown.enter.prevent="onFaceEnter"
                placeholder="7,50"
              />
            </div>

            <div class="flex-fill" v-if="facesForm.arr.length > 1">
              <label class="mini-label">Entre 2 e 3 (m)</label>
              <input
                class="ctrl"
                v-if="facesForm.arr.length > 1"
                v-model="facesForm.arr[1]"
                @focus="onFaceFocus"
                @blur="onFaceBlur"
                @keydown.enter.prevent="onFaceEnter"
                placeholder="7,50"
              />
            </div>
          </div>

          <!-- Se algum combo futuro tiver 4 peças -->
          <div class="row two" style="margin-top:6px" v-if="facesForm.arr.length > 2">
            <div class="flex-fill">
              <label class="mini-label">Entre 3 e 4 (m)</label>
              <input
                class="ctrl"
                v-if="facesForm.arr.length > 1"
                v-model="facesForm.arr[2]"
                @focus="onFaceFocus"
                @blur="onFaceBlur"
                @keydown.enter.prevent="onFaceEnter"
                placeholder="7,50"
              />
            </div>
            <div class="flex-fill"></div>
          </div>

          <div class="hint" style="margin-top:6px">
            Altere os valores; o combo reposiciona automaticamente.
          </div>
        </div>

        <div class="row two">
          <button class="btn btn-slim" @click="applyLists">Aplicar</button>
          <button class="btn btn-slim warn" @click="selected.local = {}">Repor globais</button>
        </div>

        <div class="hint mono wrap">
          c2c:
          <strong v-if="c2c.length">
            {{ c2c.map(v => formatDecimalBR(v, 2)).join(' | ') }}
          </strong>
          <span v-else>—</span>
        </div>

        <div class="hint mono wrap">
          Alturas:
          <strong v-if="heights.length">
            {{ heights.map(v => formatDecimalBR(v, 2)).join(' / ') }}
          </strong>
          <span v-else>—</span>
        </div>

        <div class="section-individual mt8">
          <div class="mini-label up">PERCURSO (AJUSTES LOCAIS)</div>

          <div class="row one">
            <label class="mini-label">Curvatura</label>
            <input type="range" min="0" max="1" step="0.01" v-model.number="uiTension" :disabled="!inCourse" />
          </div>

          <div class="row one">
            <label class="mini-label">Suavidade</label>
            <input type="range" min="0" max="1" step="0.01" v-model.number="uiSmooth" :disabled="!inCourse" />
          </div>

          <div class="row two">
            <div class="flex-fill">
              <label class="mini-label">Saída reta (passadas)</label>
              <input
                class="ctrl"
                inputmode="decimal"
                pattern="[0-9]+([,][0-9]+)?"
                :value="formCourse.exit"
                @focus="onExitFocus"
                @input="onExitInput"
                @blur="onExitBlur"
                :disabled="!inCourse"
              />
            </div>
            <div class="flex-fill">
              <label class="mini-label">Entrada reta (passadas)</label>
              <input
                class="ctrl"
                inputmode="decimal"
                pattern="[0-9]+([,][0-9]+)?"
                :value="formCourse.entry"
                @focus="onEntryFocus"
                @input="onEntryInput"
                @blur="onEntryBlur"
                :disabled="!inCourse"
              />
            </div>
          </div>

          <div class="row one">
            <div class="flex-fill">
              <label class="mini-label">Raio</label>
              <input
                class="ctrl"
                inputmode="decimal"
                pattern="[0-9]+([,][0-9]+)?"
                :value="formCourse.rmin"
                @focus="onRMinFocus"
                @input="onRMinInput"
                @blur="onRMinBlur"
                :disabled="!inCourse"
              />
            </div>
          </div>

          <div class="hint" v-if="!inCourse">Este obstáculo ainda não está no percurso.</div>
        </div>

      </div>
    </template>
  </div>
</template>

<style scoped>
.compact { --fg:#e8eefc; --fg-soft:#cfe2ff; --muted:#9fb5d9; --border:#2a3a5a; --bg-in:#0b1426; }

.compact .label { color: var(--fg); font-weight: 700; }
.compact .mini-label {
  color: var(--fg-soft);
  font-size: 11.5px;
  letter-spacing: .2px;
  display:block;
  margin-bottom: 4px;
  opacity: .95;
}

.mt6{ margin-top: 6px; }
.mt8{ margin-top: 8px; }

.row { display: grid; gap: 6px; align-items: center; margin-bottom: 8px; }
.row.one  { grid-template-columns: 1fr; }
.row.two  { grid-template-columns: 1fr 1fr; }
.row.three{ grid-template-columns: 1fr 1fr 1fr; }

.ctrl{
  width:100%;
  height: 28px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-in);
  color: var(--fg);
  font-size: 12px;
  text-align: right;
}
.ctrl:focus{ outline: none; box-shadow: 0 0 0 2px rgba(138,182,255,.25); }

.mono{ font-family: ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono",monospace; }
.wrap{ white-space: normal; word-break: break-word; }

.btn{ background:#1a2a4a; color:#e8eefc; border:1px solid var(--border); border-radius:10px; cursor:pointer; }
.btn:hover{ background:#22365e; }
.btn.warn{ background:#3a2a10; border-color:#5a3a18; }
.btn-slim{ height: 28px; padding: 0 10px; font-size: 12px; border-radius: 8px; }

.hint{ font-size: 12px; opacity: .8; color: var(--muted); }
.kv{ display:grid; grid-template-columns: auto 1fr; gap:6px; align-items: baseline; }
.kv .k{ color: var(--fg-soft); }
.kv .v{ color: var(--fg); }

/* “card” interno */
.section-individual{
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 8px;
  padding: 8px;
  background: rgba(255,255,255,.03);
  box-shadow: inset 0 0 8px rgba(0,0,0,.3);
}

/* responsivo estreito */
@media (max-width: 420px){
  .row.two{ grid-template-columns: 1fr; }
}

.up { text-transform: uppercase; letter-spacing: .4px; }

/* Harmoniza espaçamentos sem mudar o tema global */
.combo-faces { margin-top: 8px; }
.combo-faces .label { margin-bottom: 4px; }
.combo-faces .mini-label { margin-bottom: 4px; }

/* Opcional: garantir altura dos inputs como nos demais */
.combo-faces .form-control {
  height: 32px;
  padding: 4px 8px;
  line-height: 24px;
}

</style>
